/**
 * Admin API client.
 *
 * The session lives in httpOnly cookies, so nothing here holds a token — an XSS
 * bug cannot exfiltrate one. The CSRF token IS readable (that is the point of
 * the double-submit pattern) and is echoed back in a header on every mutation.
 *
 * On a 401 the client attempts one silent refresh and replays the request, so a
 * short access-token lifetime never interrupts someone mid-edit.
 */
// Empty = same origin. The admin server proxies /api/* to the API, so there
// is no build-time URL to bake in, no CORS preflight, and the identical build
// works in dev, Docker and production.
const BASE_URL = '';

export class ApiError extends Error {
  constructor(message, status, details) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

function readCookie(name) {
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`))
    ?.split('=')[1];
}

let refreshInFlight = null;

async function refreshSession() {
  // Collapse concurrent 401s into a single refresh.
  refreshInFlight ??= fetch(`${BASE_URL}/api/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  }).finally(() => {
    refreshInFlight = null;
  });

  const response = await refreshInFlight;
  return response.ok;
}

async function rawRequest(path, { method, body, signal, headers }) {
  const csrfToken = readCookie('agenz_csrf');

  return fetch(`${BASE_URL}${path}`, {
    method,
    credentials: 'include',
    signal,
    headers: {
      accept: 'application/json',
      ...(body ? { 'content-type': 'application/json' } : {}),
      ...(csrfToken && method !== 'GET' ? { 'x-csrf-token': decodeURIComponent(csrfToken) } : {}),
      ...headers,
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
}

async function request(path, { method = 'GET', body, signal, headers = {}, retry = true } = {}) {
  let response = await rawRequest(path, { method, body, signal, headers });

  if (response.status === 401 && retry && !path.startsWith('/api/auth/')) {
    if (await refreshSession()) {
      response = await rawRequest(path, { method, body, signal, headers });
    }
  }

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await response.json().catch(() => null) : await response.text();

  if (!response.ok) {
    throw new ApiError(
      payload?.message ?? `Request failed with ${response.status}`,
      response.status,
      payload?.details,
    );
  }

  return payload;
}

export const api = {
  get: (path, options) => request(path, { ...options, method: 'GET' }),
  post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
  patch: (path, body, options) => request(path, { ...options, method: 'PATCH', body }),
  put: (path, body, options) => request(path, { ...options, method: 'PUT', body }),
  delete: (path, options) => request(path, { ...options, method: 'DELETE' }),
};

export default api;
